import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import jwt_decode from 'jwt-decode';
// Components
import OrdersList from 'components/orders/OrdersList';
import LoadingOverlay from 'components/common/LoadingOverlay';
import ViewToggle from 'components/common/ViewToggle';
import SortGroupSection from 'components/orders/SortGroupSection';
import BySku from 'components/groupedBySku/BySku';
import ByZone from 'components/groupedByZone/ByZone';
// Helpers
import {
  convertQueryParamsIntoObject,
  convertObjectIntoQueryParams,
  formatDateTime,
} from 'constants/Helpers';
import { useOnScreen } from 'components/common/hooks/useOnScreen';
// Selectors
import { getIsLoaderOpen } from 'store/app/AppSelectors';
import {
  getDeliveryData,
  getorderDetailsData,
  getPageCount,
  getGroupBySkuData,
  getGroupByZoneData,
} from 'store/orders/OrdersListSelectors';
// API
import OrdersListService from 'services/OrdersListService';
// Actions
import { setLoaderOpenTrue, setLoaderOpenFalse } from 'store/app/AppActions';
// urls
import { getOrdersPageUrl, getMyTasksPageUrl } from 'constants/AppUrls';
import { getFromLocalStorage } from 'helpers/LocalStorage';

// The grouped-by endpoints are still slot scoped - 0 asks for every slot.
const ALL_SLOTS = 0;

const HFMyTasksPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const ref = useRef();
  const ordersListData = useSelector((state) => getorderDetailsData({ state }));
  const deliveryDate = useSelector((state) => getDeliveryData({ state }));
  const pageCount = useSelector((state) => getPageCount({ state }));
  const [counter, setCounter] = useState(1);
  const isLoaderOpen = useSelector((state) => getIsLoaderOpen({ state }));
  const queryObj = location.search ? convertQueryParamsIntoObject(location.search) : {};
  const onScreen = useOnScreen(ref, '200px');
  const [initialParams, setInitialParams] = useState({
    page: 1,
    deliveryDate: formatDateTime(deliveryDate),
    floristId: -1,
    sortBy: 6,
    FloristOrdersGroupBy: -1,
  });
  const [currentView, setCurrentView] = useState(1);
  const floristId =
    getFromLocalStorage('floristId') || jwt_decode(getFromLocalStorage('token'))?.id;
  const groupBySkuData = useSelector((state) => getGroupBySkuData({ state }));
  const groupByZoneData = useSelector((state) => getGroupByZoneData({ state }));

  const fetchMoreData = async (params, page, isSortBy = false) => {
    let clonedParams = { ...params, page: counter };
    if (page) {
      setCounter(1);
      clonedParams = { ...params, page: 1, floristId: +floristId };
    }

    if (page || counter <= pageCount || isSortBy) {
      dispatch(setLoaderOpenTrue());
      try {
        switch (parseInt(params.FloristOrdersGroupBy, 10)) {
          case -1:
            const resDef = await OrdersListService.getordersListData(clonedParams);
            dispatch({
              type: 'SET_ORDERS_LIST_DATA',
              payload: {
                ordersList: resDef?.data?.data,
                pageCount: resDef?.data?.pageCount,
                isFlorist: page ? true : false,
              },
            });
            break;
          case 0:
            const resSKU = await OrdersListService.getGroupedBySku(clonedParams, ALL_SLOTS);
            if (page) {
              dispatch({
                type: 'SET_GROUP_BY_SKU_DATA',
                payload: resSKU?.data,
              });
            } else {
              dispatch({
                type: 'SET_GROUP_BY_SKU_APPENDED_DATA',
                payload: resSKU?.data,
              });
            }
            break;
          case 1:
            const resZone = await OrdersListService.getGroupedByZone(clonedParams, ALL_SLOTS);
            if (page) {
              dispatch({
                type: 'SET_GROUP_BY_ZONE_DATA',
                payload: resZone?.data,
              });
            } else {
              dispatch({
                type: 'SET_GROUP_BY_ZONE_APPENDED_DATA',
                payload: resZone?.data,
              });
            }
            break;

          default:
            break;
        }
        navigate({
          pathname: '/my-task',
          search: convertObjectIntoQueryParams(clonedParams),
        });
        setCounter((prev) => prev + 1);
        dispatch(setLoaderOpenFalse());
      } catch (err) {
        dispatch(setLoaderOpenFalse());
      }
    }
  };

  const fetchData = (page) => {
    setInitialParams(queryObj);
    return fetchMoreData(queryObj, page);
  };

  const onViewChange = (vIndx, vURL) => {
    setCurrentView(vIndx);
    navigate({
      pathname: vURL,
      search: convertObjectIntoQueryParams({
        sortBy: queryObj?.sortBy || 6,
        deliveryDate: queryObj?.deliveryDate,
        floristId: -1,
        searchKey: queryObj?.searchKey || '',
        FloristOrdersGroupBy: queryObj?.FloristOrdersGroupBy || -1,
      }),
    });
  };

  const handleGroupReturnedComponent = (groupBy) => {
    switch (parseInt(groupBy, 10)) {
      case -1:
        return (
          <OrdersList
            ordersListData={ordersListData}
            fetchMoreData={fetchData}
            initialParams={initialParams}
            onScreen={onScreen}
            isMyTask={true}
          />
        );
      case 0:
        return (
          <BySku
            navigate={navigate}
            fetchMoreData={fetchData}
            initialParams={initialParams}
            groupBySkuData={groupBySkuData}
            onScreen={onScreen}
            isLoaderOpen={isLoaderOpen}
            isMyTask={true}
          />
        );
      case 1:
        return (
          <ByZone
            navigate={navigate}
            fetchMoreData={fetchData}
            initialParams={initialParams}
            groupByZoneData={groupByZoneData}
            onScreen={onScreen}
            isLoaderOpen={isLoaderOpen}
            isMyTask={true}
          />
        );

      default:
        break;
    }
  };

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryObj.FloristOrdersGroupBy]);

  return (
    <>
      {isLoaderOpen && <LoadingOverlay />}
      <SortGroupSection
        fetchData={fetchMoreData}
        setInitialParams={setInitialParams}
        baseURL={getMyTasksPageUrl()}
      />
      <ViewToggle activeView={currentView} onViewChange={onViewChange}>
        <div label="My Tasks" url={getMyTasksPageUrl()} viewIndex={1} />
        <div label="All Orders" url={getOrdersPageUrl()} viewIndex={2} />
      </ViewToggle>
      {handleGroupReturnedComponent(initialParams.FloristOrdersGroupBy)}
      <div ref={ref} />
    </>
  );
};

export default HFMyTasksPage;
